import React, { useEffect, useState } from 'react'
import { objectOmit, getValueFromObject } from '@helpers/utils'
import Modal from '@components/ui/Modal'
import ModalProductCard from '@components/ui/Modal/ModalProductCard'
import { returnsClickEvent } from '@components/returns/helpers'
import { Values } from '@components/returns/types'
import {
  ExchangeColor,
  ProductSizes,
  Return,
  ReturnReason,
  ReturnsProduct
} from '@api/returns/types'
import ExchangeForm from './exchange/ExchangeForm'
import RefundForm from './refund/RefundForm'
import styles from './styles.module.scss'
import ReturnsContext from '../context'
import { Fit } from 'types/fit'
import { SetProductToReturn } from '../ReturnsContent/ProductCardFooter'
import { CloseReturnsFormModalProps } from '..'
import { getProductColors } from '@api/product'

const returnComponentMap = {
  refund: RefundForm,
  exchange: ExchangeForm
} as const

export type PartialState<T> = {
  [K in keyof T]?: T[K]
}

function filterReasons({
  reasons,
  isExchangeable
}: {
  reasons?: ReturnReason[]
  isExchangeable: boolean
}) {
  if (!reasons?.length) return []

  if (!isExchangeable) {
    return reasons.filter(reason => !reason.title.includes('colour'))
  }

  return reasons
}

function useStateWithPartialState<T>(
  initialState: T
): [T, (partialState: PartialState<T>) => void] {
  const [state, setState] = useState<T>(initialState)

  const setPartialState = (partialState: PartialState<T>) => {
    setState(prevState => ({
      ...prevState,
      ...partialState
    }))
  }

  return [state, setPartialState]
}

export type Method = keyof typeof returnComponentMap

export type SelectedProductReturn = {
  sizes: Partial<ProductSizes>
  color: ExchangeColor
}

export type ValuesState = Values & {
  sizes: (Values['sizes'] & { available?: boolean }) | Record<string, never>
}

export type ReturnData = Return & ValuesState

type ReturnsFormModalProps = {
  product:
    | ReturnsProduct
    | (undefined & {
        return?: SelectedProductReturn
      })
  customerFit?: Fit
  returnData?: ReturnData
  onClose: CloseReturnsFormModalProps
  setProductToReturn: SetProductToReturn
  method: Method
  reasons?: ReturnReason[]
}

function ReturnsFormModal({
  onClose,
  product,
  customerFit,
  method,
  returnData,
  setProductToReturn
}: ReturnsFormModalProps) {
  const [storedSavedFit, setStoredSavedFit] = useState({})
  const [exchangeColors, setExchangeColors] = useState<ExchangeColor[]>([])
  const [values, setValues] = useStateWithPartialState<ValuesState>({
    method,
    sizes: { ...(product.return?.sizes || product.sizes), available: true },
    color: product.return?.color,
    reason: returnData?.reason,
    reason_detail: returnData?.reason_detail || []
  })
  const [stepIndex, setStepIndex] = useState(0)

  const productImage =
    getValueFromObject<string>(values, 'color.image.src', '') || product.image

  const productProperties = {
    color: product.color,
    ...product.sizes
  }
  const newProductPropertiesToCompare = {
    color: values.color?.title,
    ...objectOmit(values.sizes, ['available'])
  }

  const handleDone = () => {
    onClose({
      id: product.id,
      values: {
        ...values,
        sizes: objectOmit(values.sizes, ['available'])
      } as ReturnData
    })
  }

  const setProductToExchange = ({
    step = 0,
    savedFitsizes
  }: {
    step: number
    savedFitsizes?: Fit
  }) => {
    returnsClickEvent({
      label: "OK, I'll exchange"
    })
    setProductToReturn(product.id, 'exchange')
    setValues({
      ...storedSavedFit,
      method: 'exchange'
    })
    setStepIndex(step)
    if (savedFitsizes) {
      setStoredSavedFit(savedFitsizes)
    }
  }

  const handleSetValues = (selection: PartialState<ValuesState>) => {
    setValues(selection)
  }

  useEffect(() => {
    const fetchColours = async () => {
      try {
        const { productsByType } = await getProductColors({
          productType: product.product_type
        })
        const match = productsByType.product_types.find(
          t => t.product_type === product.product_type
        )
        setExchangeColors(match?.items ?? [])
      } catch (error) {
        console.error('Exchange Colors Error: ', error)
      }
    }

    if (method === 'exchange' && product.isExchangeable) {
      fetchColours()
    }
  }, [])

  const selectedProduct = {
    ...product,
    exchange_colors: exchangeColors
  }

  const Form = returnComponentMap[method]

  if (!Form) {
    return null
  }

  return (
    <ReturnsContext.Provider
      value={{ customerFit, setStoredSavedFit, storedSavedFit }}
    >
      <Modal
        classes={styles.modal__returns}
        onClose={onClose}
        openedModal
        content={
          <div className={styles.returnsModal} role="presentation">
            <ModalProductCard
              image={productImage}
              productType={product.product_type}
              productProperties={productProperties}
              newProductPropertiesToCompare={newProductPropertiesToCompare}
              classes={styles.returns_ModalProductCard}
            />
            <div
              className={`form ${styles.returnsModal__form} grid`}
              data-testid="reason-drop-down"
            >
              <Form
                selectedProduct={selectedProduct}
                values={values}
                setValues={handleSetValues}
                onDone={handleDone}
                stepIndex={stepIndex}
                setProductToExchange={setProductToExchange}
                reasons={filterReasons({
                  reasons: product.return_reasons,
                  isExchangeable: product.isExchangeable
                })}
              />
            </div>
          </div>
        }
      />
    </ReturnsContext.Provider>
  )
}

export default ReturnsFormModal
